// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import {
    ApiHandler,
    createHandlerChainRunner,
    HasOnError,
    HasOnSuccess,
    markSkipOnCompleteIfOnErrorIsSet,
    markSkipOnCompleteIfOnSuccessIsSet,
} from "@insite/client-framework/HandlerCreator";

import { ApiParameter } from "@insite/client-framework/Services/ApiService";
import xLoadingOverlay from "@steiner/App/Handlers/XNG/LoadingOverlay";
import {
    CheckoutRecaptchaValidateResponse,
    getCheckoutRecaptchaValidate,
} from "@steiner/App/Services/AddCheckoutRecaptcha";

export interface ValidateCheckoutRecaptchaApiParameter extends ApiParameter {
    token: string;
}

type HandlerType = ApiHandler<
    ValidateCheckoutRecaptchaApiParameter & HasOnSuccess<CheckoutRecaptchaValidateResponse> & HasOnError<string>,
    CheckoutRecaptchaValidateResponse
>;

export const BeginValidateCheckoutRecaptcha: HandlerType = props => {
    props.dispatch(xLoadingOverlay({ key: "add", id: "ValidateCheckoutRecaptcha" }));
};

export const PopulateApiParameter: HandlerType = props => {
    props.apiParameter = {
        ...props.parameter,
    };
};

export const GetCheckoutRecaptchaValidation: HandlerType = async props => {
    try {
        props.apiResult = await getCheckoutRecaptchaValidate(props.parameter.token);
    } catch (error) {
        props.dispatch(xLoadingOverlay({ key: "remove", id: "ValidateCheckoutRecaptcha" }));
        markSkipOnCompleteIfOnErrorIsSet(props);
        const message =
            error?.errorJson?.message || error?.errorMessage || "error";
        props.parameter.onError?.(message);
    }
};

export const HandleInvalidCheckoutRecaptcha: HandlerType = props => {
    if (!props.apiResult || props.apiResult.Valid) {
        return;
    }
    props.dispatch(xLoadingOverlay({ key: "remove", id: "ValidateCheckoutRecaptcha" }));
    markSkipOnCompleteIfOnErrorIsSet(props);
    props.parameter.onError?.(props.apiResult.Message || "error");
};

export const ExecuteCheckoutRecaptchaOnSuccess: HandlerType = props => {
    if (!props.apiResult?.Valid) {
        return;
    }
    props.dispatch(xLoadingOverlay({ key: "remove", id: "ValidateCheckoutRecaptcha" }));
    markSkipOnCompleteIfOnSuccessIsSet(props);
    props.parameter.onSuccess?.(props.apiResult);
};

export const chain = [
    BeginValidateCheckoutRecaptcha,
    PopulateApiParameter,
    GetCheckoutRecaptchaValidation,
    HandleInvalidCheckoutRecaptcha,
    ExecuteCheckoutRecaptchaOnSuccess,
];

const validateCheckoutRecaptcha = createHandlerChainRunner(chain, "ValidateCheckoutRecaptcha");

export default validateCheckoutRecaptcha;
